import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import React, { useState } from "react";
import { AXIOS_POST } from "../../server/api/crud";
import Swal from "sweetalert2";
import Dropzone2 from "../Dropzone2";
import { buildEmailData, emailDocumentenUpload, emailHTML } from "../../EmailTemplates";
import { documentenKeuzen, sendEmail, sendEmailGewoon } from "../../function";
import SelectAdmins2 from "../SelectAdmins2";
import { getAdmins } from "../../Localstorage/localstorage";

export default function UploadDocumenten({
  open = false,
  setOpen,
  setRefresh,
  user,
  patient,
  type = "uploads",
}) {
  const admins = getAdmins();
  const [selectedAdmin, setSelectedAdmin] = useState(admins[0]);

  const [progresses, setProgresses] = useState({}); // Track progress for each file
  const [documenten, setDocumenten] = useState([]);

  const reset = () => {
    setProgresses({});
    setDocumenten([]);
    setOpen(false);
  };

  const saveDocuments = async () => {
    if (!documenten || documenten.length === 0) return;

    if (!selectedAdmin && user?.role_id == 2) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: `Selecteer een specialist aan wie u toegang tot deze documenten wilt verlenen.`,
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    // Split documents into valid (<=6MB) and tooLarge (>6MB)
    const tooLargeDocuments = documenten.filter(
      (doc) => doc.size > 150 * 1024 * 1024
    );
    const validDocuments = documenten.filter(
      (doc) => doc.size <= 150 * 1024 * 1024
    );

    if (validDocuments.length === 0 && tooLargeDocuments.length > 0) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: `Alle bestanden zijn groter dan 150 MB en zijn niet opgeslagen.`,
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    const finalDocument = validDocuments.map((document) => ({
      ...document,
      title: progresses[document?.bestand]?.title || document.title,
    }));

    const url =
      user?.role_id === 2
        ? "documents/multiple"
        : type === "advies"
        ? "adviezen/multiple"
        : "documents/admin/multiple";

    const pagina = documentenKeuzen(user?.role_id)[type];

    try {
      const resp = await AXIOS_POST(url, { documenten: finalDocument });

      if (resp.data.success) {
        setRefresh(true);
        // Show success alert for saved files
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: `Bestanden zijn opgeslagen${
            tooLargeDocuments.length > 0
              ? `, ${tooLargeDocuments.length} bestand(en) waren te groot (>6MB) en zijn niet opgeslagen`
              : ""
          }`,
          showConfirmButton: false,
          timer: 500,
        });

        reset();

        const recipient =
          (user?.role_id === 1 && patient?.email && patient) ||
          (user?.role_id === 2 && selectedAdmin?.email && selectedAdmin);

        if (recipient) {
          const emailData = buildEmailData({
            from_name: "RepeatConnect",
            receiver: recipient.full_name,
            title: `Hallo ${recipient.full_name}, U heeft ${
              tooLargeDocuments?.length > 0
                ? `, ${tooLargeDocuments.length} bestanden ontvangen!`
                : "een bestand ontvangen!"
            }`,
            receiver_email: recipient.email,
            object: pagina,
            name: user?.full_name,
            bericht:
              tooLargeDocuments?.length > 1
                ? `${tooLargeDocuments.length} documenten`
                : "een document",
          });

          const emailHtml = emailDocumentenUpload(emailData);

          sendEmailGewoon("template_qdoh9mv", {
            message_html: emailHtml,
            title: emailData.title,
            receiver_email: emailData.receiver_email,
          });
        } else {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: `De klant heeft nog geen e-mailadres toegevoegd aan zijn profielinstellingen.`,
            showConfirmButton: false,
            timer: 3500,
          });
        }
      }
    } catch (error) {
      console.error(
        "Error occurred:",
        error.response?.data || error.message || error
      );

      Swal.fire({
        position: "top-end",
        icon: "error",
        title: `Er is iets misgelopen`,
        showConfirmButton: false,
        timer: 2500,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        setOpen(false);
      }}
      className="relative z-10"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/70 backdrop-blur-[1px] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-3 text-center sm:items-center sm:p-4">
          <DialogPanel
            transition
            className="relative w-full transform overflow-hidden rounded-2xl border border-wa-border bg-wa-panel p-4 text-left shadow-2xl shadow-black/40 transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:max-w-5xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="w-full">
              <div className="mb-4 border-b border-wa-border pb-3">
                <h2 className="text-base font-semibold leading-6 text-wa-text sm:text-lg">
                  Laad bestanden op
                </h2>
                <p className="mt-1 text-xs text-wa-muted sm:text-sm">
                  Upload een of meerdere documenten en sla ze direct op.
                </p>
              </div>
              <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                <div className="rounded-xl border border-wa-border bg-wa-panel2/60 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-wa-muted">
                    Delen met
                  </p>
                  {user?.role_id == 2 ? (
                    <div className="mt-2 w-full sm:max-w-xs">
                      <SelectAdmins2
                        selected={selectedAdmin}
                        setSelected={setSelectedAdmin}
                        data={admins}
                      />
                    </div>
                  ) : (
                    <div className="mt-2">
                      <p className="text-sm font-semibold text-wa-text">
                        {patient?.full_name || "Patiënt"}
                      </p>
                      <p className="text-xs text-wa-muted">
                        {patient?.email || "Geen e-mailadres beschikbaar"}
                      </p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setProgresses({});
                    setDocumenten([]);
                  }}
                  className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border border-wa-border bg-wa-panel2 px-4 py-2 text-sm font-semibold text-wa-text transition hover:bg-wa-hover"
                >
                  Verwijder ze allemaal
                </button>
              </div>
              <div className="rounded-xl border border-wa-border bg-wa-panel2/70 p-3 sm:p-4">
                <Dropzone2
                  user={user}
                  setProgresses={setProgresses}
                  setDocumenten={setDocumenten}
                  patient={patient}
                  type={type}
                  progresses={progresses}
                />
              </div>
              <div className="mt-4 flex flex-col-reverse gap-2 border-t border-wa-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  onClick={() => setOpen(false)}
                  className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border border-wa-border bg-wa-panel2 px-4 py-2 text-sm font-semibold text-wa-text transition hover:bg-wa-hover"
                >
                  Sluiten
                </button>
                <button
                  onClick={saveDocuments}
                  className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg bg-wa-accent px-4 py-2 text-sm font-semibold text-wa-onaccent transition hover:bg-wa-accentdeep"
                >
                  Opslaan
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
